import { PermissionRoles } from '@gearbelt/global';
import { Button } from '@gearbelt/ui/components';
import { zodResolver } from '@hookform/resolvers/zod';
import { AccountTypes, IAccount, PaymentCurrencyTypes, PaymentMethods, PaymentTermContexts } from '@site-mate/sitemate-global-shared/lib';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Controller, useForm } from 'react-hook-form';
import { z } from 'zod';

import { SettingsFormSkeleton } from '@/components/settings/settings-form-skeleton';
import { DatePicker } from '@/components/ui/date-picker';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Select } from '@/components/ui/select';
import { Switch } from '@/components/ui/switch';
import { useToast } from '@/components/ui/toaster';
import { getSitemateStartAccountQueryOptions, sitemateStartAccountKeys } from '@/queries/account';
import { accountService } from '@/services/global-api/account.service';
import { useUserStore } from '@/stores/user.store';

const formSchema = z.object({
  billingCompany: z.string().optional(),
  billingAddress: z.string().optional(),
  billingContact: z.string().optional(),
  billingDate: z.date().optional(),
  initialSubscriptionDate: z.date().optional(),
  paymentTermDay: z.string().optional(),
  paymentTermContext: z
    .object({
      label: z.string(),
      value: z.nativeEnum(PaymentTermContexts),
    })
    .optional(),
  paymentMethod: z
    .object({
      label: z.nativeEnum(PaymentMethods),
      value: z.nativeEnum(PaymentMethods),
    })
    .optional(),
  currency: z
    .object({
      label: z.nativeEnum(PaymentCurrencyTypes),
      value: z.nativeEnum(PaymentCurrencyTypes),
    })
    .optional(),
  accountType: z
    .object({
      label: z.nativeEnum(AccountTypes),
      value: z.nativeEnum(AccountTypes),
    })
    .optional(),
  automatedBilling: z.boolean().optional(),
});

function SettingsSitemateStartGlobalBillingPage() {
  const { org } = useUserStore();

  const { data: account, isLoading } = useQuery(getSitemateStartAccountQueryOptions({ accountId: org?.accountId }));

  return (
    <div className="flex w-full max-w-[833px] flex-col gap-4 rounded-lg bg-white p-6 shadow-lg">
      <ScrollArea viewportClassName="pr-2">
        <div className="flex flex-col gap-4">
          <h1 className="text-default-text text-xl font-medium">Global billing</h1>
          <p className="text-default-text text-sm">These billing settings apply to all of your product subscriptions (not only Gearbelt).</p>
          {(isLoading || !account) && <SettingsFormSkeleton rows={11} />}
          {account && <SettingsSitemateStartGlobalBillingForm account={account} />}
        </div>
      </ScrollArea>
    </div>
  );
}

function SettingsSitemateStartGlobalBillingForm({ account }: { account: IAccount }) {
  const toast = useToast();
  const queryClient = useQueryClient();

  const { user } = useUserStore();

  const formInstance = useForm({
    resolver: zodResolver(formSchema),
    defaultValues: {
      billingCompany: account.billing?.billingCompanyName ?? '',
      billingAddress: account.billing?.billingAddress ?? '',
      billingContact: account.billing?.billingContact ?? '',
      billingDate: account.billing?.billingDate ? new Date(account.billing.billingDate) : undefined,
      initialSubscriptionDate: account.billing?.initialSubscriptionDate ? new Date(account.billing.initialSubscriptionDate) : undefined,
      paymentTermDay: account.billing?.paymentTermDay?.toString() ?? '',
      paymentTermContext: account.billing?.paymentTermContext
        ? {
            label: account.billing.paymentTermContext.charAt(0).toUpperCase() + account.billing.paymentTermContext.slice(1),
            value: account.billing.paymentTermContext,
          }
        : undefined,
      paymentMethod: account.billing?.paymentMethod
        ? {
            label: account.billing.paymentMethod,
            value: account.billing.paymentMethod,
          }
        : undefined,
      currency: account.billing?.currency
        ? {
            label: account.billing.currency,
            value: account.billing.currency,
          }
        : undefined,
      accountType: account.accountType
        ? {
            label: account.accountType,
            value: account.accountType,
          }
        : undefined,
      automatedBilling: account.automatedBilling ?? false,
    },
  });
  const {
    control,
    handleSubmit,
    formState: { isValid, isSubmitting, isDirty },
  } = formInstance;

  const { mutateAsync: updateAccount } = useMutation({
    mutationFn: accountService.updateAccountBillingSettings,
    onSuccess: async () => {
      toast.success('Your settings have been updated successfully');
      await queryClient.invalidateQueries({
        queryKey: sitemateStartAccountKeys.detail(account._id),
      });
    },
    onError: () => {
      toast.error('An error occurred while updating the settings.');
    },
  });

  async function onSubmit(data: z.infer<typeof formSchema>) {
    await updateAccount({
      _id: account._id,
      accountType: data.accountType?.value,
      automatedBilling: data.automatedBilling,
      billing: {
        billingCompanyName: data.billingCompany,
        billingAddress: data.billingAddress,
        billingContact: data.billingContact,
        billingDate: data.billingDate?.toISOString(),
        initialSubscriptionDate: data.initialSubscriptionDate?.toISOString(),
        paymentTermDay: data.paymentTermDay ? +data.paymentTermDay : undefined,
        paymentTermContext: data.paymentTermContext?.value,
        paymentMethod: data.paymentMethod?.value,
        currency: data.currency?.value,
      },
    });
  }

  return (
    <form onSubmit={handleSubmit(onSubmit)} className="flex flex-col gap-4">
      <Controller
        control={control}
        name="billingCompany"
        render={({ field: { value, onChange }, fieldState: { error } }) => (
          <Label className="flex items-center gap-1">
            <div className="w-1/3 shrink-0">Billing company</div>
            <Input
              placeholder="Enter billing company..."
              value={value}
              onChange={(e) => onChange(e.currentTarget.value)}
              error={!!error}
              className="flex-1"
              disabled={!user?.roles.includes(PermissionRoles.SitemateSuperUser)}
            />
          </Label>
        )}
      />

      <Controller
        control={control}
        name="billingAddress"
        render={({ field: { value, onChange }, fieldState: { error } }) => (
          <Label className="flex items-center gap-1">
            <div className="w-1/3 shrink-0">Billing address</div>
            <Input
              placeholder="Enter billing address..."
              value={value}
              onChange={(e) => onChange(e.currentTarget.value)}
              error={!!error}
              className="flex-1"
              disabled={!user?.roles.includes(PermissionRoles.SitemateSuperUser)}
            />
          </Label>
        )}
      />

      <Controller
        control={control}
        name="billingContact"
        render={({ field: { value, onChange }, fieldState: { error } }) => (
          <Label className="flex items-center gap-1">
            <div className="w-1/3 shrink-0">Billing contact</div>
            <Input
              placeholder="Enter billing contact..."
              value={value}
              onChange={(e) => onChange(e.currentTarget.value)}
              error={!!error}
              className="flex-1"
              disabled={!user?.roles.includes(PermissionRoles.SitemateSuperUser)}
            />
          </Label>
        )}
      />

      <Controller
        control={control}
        name="billingDate"
        render={({ field: { value, onChange }, fieldState: { error } }) => (
          <Label className="flex items-center gap-1">
            <div className="w-1/3 shrink-0">Billing date</div>
            <DatePicker
              placeholder="Select billing date..."
              date={value}
              setDate={(date) => onChange(date)}
              error={!!error}
              disabled={!user?.roles.includes(PermissionRoles.SitemateSuperUser)}
            />
          </Label>
        )}
      />

      <Controller
        control={control}
        name="initialSubscriptionDate"
        render={({ field: { value, onChange }, fieldState: { error } }) => (
          <Label className="flex items-center gap-1">
            <div className="w-1/3 shrink-0">Initial subscription date</div>
            <DatePicker
              placeholder="Select initial subscription date..."
              date={value}
              setDate={(date) => onChange(date)}
              error={!!error}
              disabled={!user?.roles.includes(PermissionRoles.SitemateSuperUser)}
            />
          </Label>
        )}
      />

      <div className="flex items-center gap-1">
        <div className="text-default-text w-1/3 shrink-0 text-sm">Payment term</div>
        <div className="flex flex-1 gap-2">
          <Controller
            control={control}
            name="paymentTermDay"
            render={({ field: { value, onChange }, fieldState: { error } }) => (
              <Select
                aria-label="Term day"
                className="w-1/2"
                placeholder="Select term day..."
                value={
                  value
                    ? {
                        label: value,
                        value: value,
                      }
                    : undefined
                }
                onChange={(option) => onChange(option?.value)}
                options={Array.from({ length: 61 }, (_, i) => i).map((day) => ({
                  label: day.toString(),
                  value: day.toString(),
                }))}
                error={!!error}
                isDisabled={!user?.roles.includes(PermissionRoles.SitemateSuperUser)}
              />
            )}
          />

          <Controller
            control={control}
            name="paymentTermContext"
            render={({ field: { value, onChange }, fieldState: { error } }) => (
              <Select
                aria-label="Term context"
                className="w-1/2"
                placeholder="Select term context..."
                value={value}
                onChange={onChange}
                options={Object.values(PaymentTermContexts).map((context) => ({
                  label: context.charAt(0).toUpperCase() + context.slice(1),
                  value: context,
                }))}
                error={!!error}
                isDisabled={!user?.roles.includes(PermissionRoles.SitemateSuperUser)}
              />
            )}
          />
        </div>
      </div>

      <Controller
        control={control}
        name="paymentMethod"
        render={({ field: { value, onChange }, fieldState: { error } }) => (
          <Label className="flex items-center gap-1">
            <div className="w-1/3 shrink-0">Payment method</div>
            <Select
              aria-label="Payment method"
              placeholder="Select payment method..."
              className="flex-1"
              value={value}
              onChange={onChange}
              options={Object.values(PaymentMethods).map((method) => ({ label: method, value: method }))}
              error={!!error}
              isDisabled={!user?.roles.includes(PermissionRoles.SitemateSuperUser)}
            />
          </Label>
        )}
      />

      <Controller
        control={control}
        name="currency"
        render={({ field: { value, onChange }, fieldState: { error } }) => (
          <Label className="flex items-center gap-1">
            <div className="w-1/3 shrink-0">Currency</div>
            <Select
              aria-label="Currency"
              placeholder="Select currency..."
              className="flex-1"
              value={value}
              onChange={onChange}
              options={Object.values(PaymentCurrencyTypes).map((currency) => ({ label: currency, value: currency }))}
              error={!!error}
              isDisabled={!user?.roles.includes(PermissionRoles.SitemateSuperUser)}
            />
          </Label>
        )}
      />

      <Controller
        control={control}
        name="accountType"
        render={({ field: { value, onChange }, fieldState: { error } }) => (
          <Label className="flex items-center gap-1">
            <div className="w-1/3 shrink-0">Account type</div>
            <Select
              aria-label="Account type"
              placeholder="Select account type..."
              className="flex-1"
              value={value}
              onChange={onChange}
              options={Object.values(AccountTypes).map((type) => ({ label: type, value: type }))}
              error={!!error}
              isDisabled={!user?.roles.includes(PermissionRoles.SitemateSuperUser)}
            />
          </Label>
        )}
      />

      <Controller
        control={control}
        name="automatedBilling"
        render={({ field: { value, onChange } }) => (
          <Label className="flex items-center gap-1">
            <div className="w-1/3 shrink-0">Automated billing</div>
            <Switch checked={value || false} onCheckedChange={onChange} disabled={!user?.roles.includes(PermissionRoles.SitemateSuperUser)} />
          </Label>
        )}
      />

      {user?.roles.includes(PermissionRoles.SitemateSuperUser) && (
        <div className="col-12">
          <Button type="submit" variant="success" className="mt-2" disabled={!isValid || isSubmitting || !isDirty} loading={isSubmitting}>
            Save changes
          </Button>
        </div>
      )}
    </form>
  );
}

export default SettingsSitemateStartGlobalBillingPage;
